import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Skeleton, notification } from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  LockOutlined,
  ReloadOutlined,
  SaveOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import {
  FeaturePending,
  LoadFailed,
  apiErrorCode,
  apiErrorText,
  fmtDateTime,
  fmtNumber,
  isMissingEndpoint,
  unwrap,
} from 'pages/access-groups/common';
import Matrix, { readSlot, slotsOf } from './matrix';
import { ResetModal, SaveModal } from './confirm-modals';
import { useLeaveGuard } from './use-leave-guard';
import {
  DraftChange,
  PermissionAction,
  PermissionChange,
  PermissionMatrixView,
  PermissionSaveBody,
  PermissionSlot,
  Tier,
} from './types';
import styles from './styles.module.scss';

/**
 * Trang "Quyền hạn" (/access/permissions) — ADMIN sửa, MOD chỉ xem.
 * docs/to-chuc-lai-nhom-quyen/00-ban-do-va-phuong-an-2026-09-29.md §4.3, §4.4.
 * Sửa là BẢN NHÁP: gạt công tắc chỉ đổi trên màn hình; bấm "Lưu…", đọc lại danh sách thay đổi,
 * nhập lý do thì mới gửi lên máy chủ.
 */

const TITLE = 'Quyền hạn';

const ERRORS: Record<string, string> = {
  APPROVAL_PERMISSION_LOCKED:
    'Có ô đã bị khoá nên không đổi được. Bấm Tải lại để xem bảng quyền mới nhất rồi chỉnh lại.',
  APPROVAL_PERMISSION_INVALID:
    'Bảng quyền trên máy chủ vừa đổi nên thay đổi này không còn khớp. Bấm Tải lại rồi chỉnh lại.',
  APPROVAL_REASON_REQUIRED: 'Cần nhập lý do, ít nhất 5 ký tự.',
  APPROVAL_PERMISSION_UNAVAILABLE:
    'Bảng quyền chưa được tạo trên máy chủ nên chưa lưu được. Hãy báo người vận hành máy chủ.',
  APPROVAL_ROLE_NOT_ALLOWED: 'Tài khoản của bạn không được làm việc này. Chỉ Admin sửa quyền.',
};

/**
 * Lỗi khi lưu. Với ô khoá, máy chủ gửi kèm câu nói rõ Ô NÀO và vì sao (vd. "Thu hồi tăng ca ·
 * Quản lý tổng (SMOD): Việc này chỉ Admin làm.") — giữ câu đó rồi mới thêm hướng dẫn.
 */
function saveErrorText(err: any, fallback: string): string {
  const detail = typeof err?.error === 'string' ? err.error.trim() : '';
  if (apiErrorCode(err) === 'APPROVAL_PERMISSION_LOCKED' && detail && /[^A-Z0-9_]/.test(detail)) {
    return `${detail} Ô này bị khoá nên chưa lưu thay đổi nào. Bấm Tải lại để xem bảng quyền mới nhất.`;
  }
  return apiErrorText(err, ERRORS, fallback);
}

// Lúc TẢI trang: tài khoản chưa có cấp quản lý thì máy chủ cũng trả mã này.
const LOAD_ERRORS: Record<string, string> = {
  ...ERRORS,
  APPROVAL_ROLE_NOT_ALLOWED:
    'Tài khoản của bạn chưa có cấp quản lý nên không xem được bảng quyền. Nhờ Admin kiểm tra vai trò của bạn.',
};

type Phase = 'loading' | 'ready' | 'pending' | 'error';

const looksLikeMatrix = (body: any) => Array.isArray(body?.groups) && Array.isArray(body?.tiers);

/** Duyệt mọi công tắc của bảng theo đúng thứ tự trên màn hình. */
function eachSlot(
  view: PermissionMatrixView,
  visit: (
    tier: PermissionMatrixView['tiers'][number],
    action: PermissionAction,
    slot: PermissionSlot
  ) => void
) {
  view.groups.forEach((group) => {
    group.actions.forEach((action) => {
      const slots = slotsOf(action, view.scopes);
      view.tiers.forEach((tier) => {
        slots.forEach((slot) => visit(tier, action, slot));
      });
    });
  });
}

function pruneDraft(draft: Record<string, boolean>, view: PermissionMatrixView) {
  if (!Object.keys(draft).length) return draft;
  const kept: Record<string, boolean> = {};
  eachSlot(view, (tier, action, slot) => {
    const state = readSlot(action, tier.key, slot, draft);
    if (!state.dirty) return;
    state.keys.forEach((key) => {
      kept[key] = state.value;
    });
  });
  return kept;
}

const AccessPermissionsPage = () => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [view, setView] = useState<PermissionMatrixView | null>(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [saveOpen, setSaveOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setRefreshing(true);
    AxiosUtils.get(API.approvalPermissions)
      .then((res: any) => {
        if (cancelled) return;
        const next = unwrap<PermissionMatrixView>(res, looksLikeMatrix);
        if (!next) {
          setError('Máy chủ trả về dữ liệu không đọc được. Vui lòng thử lại.');
          setPhase('error');
          return;
        }
        setView(next);
        // Bấm "Tải lại" khi đang có nháp: giữ những ô còn sửa được và vẫn khác giá trị đã lưu.
        setDraft((prev) => pruneDraft(prev, next));
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        if (isMissingEndpoint(err)) {
          setPhase('pending');
          return;
        }
        setError(apiErrorText(err, LOAD_ERRORS, 'Không tải được bảng quyền.'));
        setPhase('error');
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = () => {
    setPhase((current) => (current === 'ready' ? current : 'loading'));
    setAttempt((n) => n + 1);
  };

  // Chưa có bảng trên máy chủ thì chưa lưu được → cũng không cho gạt, tránh soạn nháp vô ích.
  const editable = !!view?.canEdit && !!view?.tableReady;

  /** Thay đổi chưa lưu, theo đúng thứ tự trên bảng, kèm chữ để đọc. */
  const changes: DraftChange[] = useMemo(() => {
    if (!view) return [];
    const list: DraftChange[] = [];
    eachSlot(view, (tier, action, slot) => {
      const state = readSlot(action, tier.key, slot, draft);
      if (!state.dirty) return;
      list.push({
        key: state.keys.join('+'),
        tier: tier.key,
        action: action.action,
        scopes: slot.scopes,
        allowed: state.value,
        tierLabel: tier.label,
        actionLabel: action.label,
        scopeLabel: slot.scopes.length > 1 ? '' : slot.label,
      });
    });
    return list;
  }, [view, draft]);

  const dirty = editable && changes.length > 0;

  useLeaveGuard(dirty, {
    title: 'Rời trang khi chưa lưu?',
    content: `Bạn có ${changes.length} thay đổi quyền chưa lưu. Rời trang thì các thay đổi này sẽ mất.`,
    okText: 'Rời trang, bỏ thay đổi',
    cancelText: 'Ở lại',
  });

  const onChange = useCallback(
    (tier: Tier, action: PermissionAction, slot: PermissionSlot, next: boolean) => {
      setDraft((prev) => {
        const state = readSlot(action, tier, slot, prev);
        if (state.locked) return prev;
        const copy = { ...prev };
        state.keys.forEach((key) => {
          // Gạt về đúng giá trị đang lưu thì coi như không đổi.
          if (next === state.saved) delete copy[key];
          else copy[key] = next;
        });
        return copy;
      });
    },
    []
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const allCollapsed =
    !!view && view.groups.length > 0 && view.groups.every((g) => collapsed[g.key]);

  const toggleAll = () => {
    if (!view) return;
    if (allCollapsed) {
      setCollapsed({});
      return;
    }
    const next: Record<string, boolean> = {};
    view.groups.forEach((group) => {
      next[group.key] = true;
    });
    setCollapsed(next);
  };

  const applyResponse = (res: any) => {
    const next = unwrap<PermissionMatrixView>(res, looksLikeMatrix);
    if (next) {
      setView(next);
      setDraft({});
    } else {
      // Máy chủ không trả lại bảng: bỏ nháp rồi tải lại để màn hình khớp dữ liệu thật.
      setDraft({});
      setAttempt((n) => n + 1);
    }
  };

  const save = async (reason: string) => {
    if (!changes.length) return;
    setSaving(true);
    setModalError('');
    try {
      const cells: PermissionChange[] = [];
      changes.forEach(({ tier, action, scopes, allowed }) => {
        scopes.forEach((scope) => cells.push({ tier, action, scope, allowed }));
      });
      const body: PermissionSaveBody = { changes: cells, reason };
      const res = await AxiosUtils.put(API.approvalPermissions, body);
      const count = changes.length;
      applyResponse(res);
      setSaveOpen(false);
      notification.success({ message: `Đã lưu ${count} thay đổi quyền.` });
    } catch (err) {
      setModalError(saveErrorText(err, 'Chưa lưu được. Vui lòng thử lại.'));
      // Ô vừa bị khoá hoặc bảng vừa đổi: tải lại nền để lần mở sau thấy đúng.
      if (apiErrorCode(err) === 'APPROVAL_PERMISSION_UNAVAILABLE') setAttempt((n) => n + 1);
    } finally {
      setSaving(false);
    }
  };

  const reset = async (reason: string) => {
    setSaving(true);
    setModalError('');
    try {
      const res = await AxiosUtils.post(API.approvalPermissionsReset, { reason });
      applyResponse(res);
      setResetOpen(false);
      notification.success({ message: 'Đã khôi phục quyền mặc định.' });
    } catch (err) {
      setModalError(apiErrorText(err, ERRORS, 'Chưa khôi phục được. Vui lòng thử lại.'));
    } finally {
      setSaving(false);
    }
  };

  let body: React.ReactNode;
  if (phase === 'loading') {
    body = (
      <div aria-busy="true" aria-label="Đang tải bảng quyền">
        <ul className={styles.tiers}>
          {[0, 1, 2].map((index) => (
            <li key={index} className={styles.tier}>
              <Skeleton active title={{ width: '55%' }} paragraph={{ rows: 1 }} />
            </li>
          ))}
        </ul>
        <div className={styles.meta} />
        <div className={styles.matrixWrap} style={{ padding: 16 }}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      </div>
    );
  } else if (phase === 'pending') {
    body = <FeaturePending onRetry={reload} />;
  } else if (phase === 'error' || !view) {
    body = <LoadFailed title="Không tải được trang Quyền hạn" text={error} onRetry={reload} />;
  } else {
    body = (
      <>
        {!view.tableReady && (
          <Alert
            className={styles.notice}
            type="warning"
            showIcon
            message="Bảng quyền chưa được tạo trên máy chủ. Đang dùng quyền mặc định; chưa lưu được thay đổi."
          />
        )}
        {!view.canEdit && (
          <Alert
            className={styles.notice}
            type="info"
            showIcon
            icon={<EyeOutlined />}
            message="Bạn chỉ xem được. Chỉ Admin sửa quyền."
          />
        )}

        <ul className={styles.tiers} aria-label="Ba cấp quản lý">
          {view.tiers.map((tier) => (
            <li key={tier.key} className={styles.tier}>
              <span className={styles.tierText}>
                <span className={styles.tierLabel}>{tier.label}</span>
                <span className={styles.tierDescription}>{tier.description}</span>
              </span>
              <span className={styles.tierCount}>
                <strong>{fmtNumber(tier.memberCount)}</strong>
                người
              </span>
            </li>
          ))}
        </ul>

        <div className={styles.meta}>
          <div className={styles.metaText}>
            <span>
              {view.updatedAt ? (
                <>
                  Cập nhật lần cuối: <strong>{fmtDateTime(view.updatedAt)}</strong>
                  {view.updatedBy?.name ? (
                    <>
                      {' '}
                      bởi <strong>{view.updatedBy.name}</strong>
                    </>
                  ) : null}
                </>
              ) : (
                'Chưa ai đổi quyền, bảng đang đúng mặc định.'
              )}
            </span>
            <span>
              <strong>{fmtNumber(view.changedCount)}</strong> ô đang khác mặc định
            </span>
          </div>
          <div className={styles.metaActions}>
            <Button size="small" onClick={toggleAll}>
              {allCollapsed ? 'Mở hết các mảng' : 'Thu gọn hết'}
            </Button>
            <Button size="small" icon={<ReloadOutlined />} loading={refreshing} onClick={reload}>
              Tải lại
            </Button>
            {editable && (
              <Button
                size="small"
                danger
                icon={<UndoOutlined />}
                onClick={() => {
                  setModalError('');
                  setResetOpen(true);
                }}
              >
                Khôi phục mặc định
              </Button>
            )}
          </div>
        </div>

        <div className={styles.legend}>
          <span>
            <span className={styles.dot} aria-hidden /> Khác mặc định
          </span>
          <span>
            <LockOutlined aria-hidden /> Ô khoá, không chỉnh được
          </span>
          {editable && (
            <span>
              <EditOutlined aria-hidden /> Gạt công tắc là sửa nháp, bấm “Lưu…” mới có tác dụng
            </span>
          )}
        </div>

        <Matrix
          view={view}
          draft={draft}
          editable={editable}
          collapsed={collapsed}
          onToggleGroup={toggleGroup}
          onChange={onChange}
        />

        {view.hardRules?.length > 0 && (
          <section className={styles.rules} aria-labelledby="perm-hard-rules">
            <h2 id="perm-hard-rules" className={styles.rulesTitle}>
              <InfoCircleOutlined style={{ color: colors.infoInk }} aria-hidden />
              Luật cố định, không chỉnh được
            </h2>
            <ol className={styles.rulesList}>
              {view.hardRules.map((rule) => (
                <li key={rule.key}>{rule.text}</li>
              ))}
            </ol>
          </section>
        )}

        {dirty && (
          <div className={styles.saveBar} role="region" aria-label="Thay đổi chưa lưu">
            <span className={styles.saveBarText} aria-live="polite">
              <EditOutlined aria-hidden />
              {changes.length} thay đổi chưa lưu
            </span>
            <span className={styles.saveBarActions}>
              <Button onClick={() => setDraft({})}>Bỏ thay đổi</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => {
                  setModalError('');
                  setSaveOpen(true);
                }}
              >
                Lưu…
              </Button>
            </span>
          </div>
        )}
      </>
    );
  }

  return (
    <div>
      <PageTitle title={TITLE} />
      {body}
      <SaveModal
        open={saveOpen}
        changes={changes}
        saving={saving}
        error={modalError}
        onCancel={() => setSaveOpen(false)}
        onSubmit={save}
      />
      <ResetModal
        open={resetOpen}
        changedCount={view?.changedCount ?? 0}
        draftCount={changes.length}
        saving={saving}
        error={modalError}
        onCancel={() => setResetOpen(false)}
        onSubmit={reset}
      />
    </div>
  );
};

export default AccessPermissionsPage;
